import { PlusIcon } from '@heroicons/react/16/solid';
import { classSet, useRect } from '@platform/ui-library';
import {
  ReactNode,
  useEffect,
  useState
} from 'react';
import { Label, PopUp } from '@app/form_components';
import { FormControlProps } from '@app/types';
import styles from './ItemList.module.scss';

export type ItemListFormProps<T> = (
  FormControlProps<T>
  & { onDelete: () => void; }
);

export type ItemListRowProps<T> = {
  styles: Record<string, string>;
  value: T;
};

export type ItemListProps<T> = (
  FormControlProps<T[]>
  & {
    getKey: (item: T) => number | string;
    label: string;
    renderForm: (props: ItemListFormProps<T>) => ReactNode;
    renderRow: (props: ItemListRowProps<T>) => ReactNode;
    tooltip?: string;
  }
);

export function ItemList<T>({
  getKey,
  label,
  name,
  onBlur,
  onChange,
  onFocus,
  renderForm,
  renderRow,
  tooltip,
  value
}: ItemListProps<T>) {
  value ??= [];
  const [setBtnRef, btnRect, updateBtnRect] = useRect<HTMLButtonElement>();
  const [setRowRef, rowRect, updateRowRect] = useRect<HTMLLIElement>();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const handleChange = (newItem: T) => {
    if (selectedIndex == null) {
      return;
    }

    const newItems = [...value];
    newItems[selectedIndex] = newItem;
    onChange(newItems);
    setSelectedIndex(null);
  };

  const handleDelete = () => {
    if (selectedIndex == null) {
      return;
    }

    onChange(value.filter((_, index) => index !== selectedIndex));
    setSelectedIndex(null);
  };

  const isAdding = selectedIndex === value.length;
  const anchorRect = (selectedIndex == null) ? null : (
    (isAdding) ? btnRect : rowRect
  );

  useEffect(() => {
    updateBtnRect();
    updateRowRect();
  }, [selectedIndex, value.length]);

  const selectedItem = (selectedIndex === null) ? null : value[selectedIndex];

  return (
    <div>
      <div
        className={classSet(['form-component-container', 'flex-center-start', 'flex-gap', styles.headerRow])}
      >
        <button
          className="form-button square"
          onClick={() => setSelectedIndex(value.length)}
          ref={setBtnRef}
          type="button"
        >
          <PlusIcon />
        </button>
        <Label classNames={{ label: 'marginless' }} tooltip={tooltip}>{label}</Label>
      </div>

      <ul className={styles.list}>
        {value.map((rowValue, index) => (
          <li
            key={getKey(rowValue)}
            className={classSet([index === selectedIndex && styles.active])}
            onClick={() => setSelectedIndex(index)}
            ref={(node) => {
              if (index === selectedIndex && !isAdding) {
                setRowRef(node);
              }
            }}
          >
            <div className={styles.mainPanel}>
              {renderRow({ value: rowValue, styles })}
            </div>
            <div className={styles.sidePanel}>
              <i className={'fe icon-edit-3'}></i>
            </div>
          </li>
        ))}
      </ul>

      {(selectedIndex == null) ? null : (
        <PopUp handleClose={() => setSelectedIndex(null)} refRect={anchorRect}>
          {renderForm({
            name: `${name}-${(!selectedItem) ? 'new' : getKey(selectedItem)}`,
            onBlur,
            onChange: handleChange,
            onDelete: handleDelete,
            onFocus,
            value: selectedItem
          })}
        </PopUp>
      )}
    </div>
  );
}
